import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";
import { ApiBackendService } from "@app/_services/api-backend.service";
import { NzMessageService } from "ng-zorro-antd/message";
import { ChartDataSets, ChartOptions } from "chart.js";
import { Label } from "ng2-charts";
import { saveAs } from "file-saver";

@Component({
  selector: "app-budget-comparison",
  templateUrl: "./budget-comparison.component.html",
  styleUrls: ["./budget-comparison.component.css"],
})
export class BudgetComparisonComponent implements OnChanges {
  @Input() budgetGroup: { name: string; realistic: any; pessimistic: any } = { name: '', realistic: null, pessimistic: null };
  @Input() availableFiles: any[] = [];
  @Output() close = new EventEmitter<void>();

  monthAssignments: Map<string, number | null> = new Map(); // "month-year" → processId
  comparisonData: any = null;
  loadingComparison = false;
  budgetGuidanceContent: string = '';
  savingAssignment = false;
  expandSet = new Set<number>(); // sort_order keys
  expandedYears = new Set<number>(); // year expand/collapse

  // Simple month+year selector
  selectedMonth: number = new Date().getMonth() + 1;
  selectedYear: number = new Date().getFullYear();
  autoMatchResult: any = null;
  checkingMatch = false;

  // List of added comparison months
  comparisonMonths: { month: number; year: number; budgetId: number; realizedProcessId: number; label: string }[] = [];
  chartChapters: { sort_order: number; description: string }[] = [];
  selectedChartSort: number | null = null;

  // Grouped bar chart (Chart.js): R / BR / BP per month for selected chapter
  barChartLabels: Label[] = [];
  barChartDatasets: ChartDataSets[] = [];
  barChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxes: [{ stacked: false }],
      yAxes: [{
        stacked: false,
        ticks: {
          callback: (value: any) => Number(value).toLocaleString('ro'),
        },
      }],
    },
    legend: { position: 'bottom', labels: { fontSize: 22 } },
    tooltips: {
      callbacks: {
        label: (item: any, data: any) => {
          const label = data.datasets[item.datasetIndex].label || '';
          const val = Number(item.yLabel).toLocaleString('ro');
          return `${label}: ${val}`;
        },
      },
    },
  };


  showEmailModal = false;
  emailAddress = '';
  sendingEmail = false;

  budgetMonths: { month: number; year: number; label: string }[] = [];
  assignedMonths: { month: number; year: number; label: string; hasRealized?: boolean }[] = [];

  // Prebuilt lookup maps from comparisonData
  private rowLookup: Map<string, any> = new Map(); // "sortOrder-month-year" → row
  private accountLookup: Map<string, any[]> = new Map(); // "sortOrder-month-year" → accounts

  months = [
    { value: 1, label: "Ian" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Iun" },
    { value: 7, label: "Iul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  constructor(
    private apiBackend: ApiBackendService,
    private message: NzMessageService,
  ) {
    this.apiBackend.getTextSlideByKey('budget_guidance').subscribe(
      (res: any) => {
        if (res.body?.content) {
          this.budgetGuidanceContent = res.body.content;
        }
      },
      () => {}
    );
  }

  get availableYears(): number[] {
    const current = new Date().getFullYear();
    return [current - 2, current - 1, current, current + 1, current + 2];
  }

  checkAutoMatch() {
    this.checkingMatch = true;
    this.autoMatchResult = null;
    this.apiBackend.getBudgetAutoMatch(this.selectedMonth, this.selectedYear).subscribe(
      (res) => {
        this.autoMatchResult = res.body;
        this.checkingMatch = false;
        // Auto-add and auto-generate if both budget and realized exist
        if (!this.autoMatchResult.missingBudget && !this.autoMatchResult.missingRealized) {
          this.addToComparison();
          this.loadComparison();
        }
      },
      (err) => {
        this.checkingMatch = false;
        this.message.error('Eroare la verificare');
      }
    );
  }

  addToComparison() {
    if (!this.autoMatchResult || this.autoMatchResult.missingBudget || this.autoMatchResult.missingRealized) return;
    const existing = this.comparisonMonths.find(
      cm => cm.month === this.selectedMonth && cm.year === this.selectedYear
    );
    if (existing) {
      if (existing.realizedProcessId !== this.autoMatchResult.realizedProcessId) {
        existing.realizedProcessId = this.autoMatchResult.realizedProcessId;
        this.message.success('Balanta actualizata cu ultima versiune incarcata');
      } else {
        this.message.warning('Aceasta luna este deja adaugata cu ultima balanta');
      }
      this.autoMatchResult = null;
      return;
    }
    const monthLabel = this.months[this.selectedMonth - 1]?.label || this.selectedMonth;
    this.comparisonMonths.push({
      month: this.selectedMonth,
      year: this.selectedYear,
      budgetId: this.autoMatchResult.budgetId,
      realizedProcessId: this.autoMatchResult.realizedProcessId,
      label: `${monthLabel} ${this.selectedYear}`,
    });
    this.autoMatchResult = null; // Reset for next selection
  }

  removeComparisonMonth(index: number) {
    this.comparisonMonths.splice(index, 1);
    if (this.comparisonMonths.length > 0) {
      this.loadComparison();
    } else {
      this.comparisonData = null;
      // Save empty assignments to backend so removed month doesn't reappear on refresh
      const budgetId = this.budgetGroup.realistic?.id || this.budgetGroup.pessimistic?.id;
      if (budgetId) {
        this.apiBackend.updateBudgetMonthAssignments(budgetId, []).subscribe();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['budgetGroup'] && this.budgetGroup) {
      this.comparisonData = null;
      this.expandSet.clear();
      this.comparisonMonths = [];
      this.autoMatchResult = null;
      this.buildMonthList();
      this.loadAssignments();
    }
  }

  buildMonthList() {
    const ref = this.budgetGroup.realistic || this.budgetGroup.pessimistic;
    if (!ref) return;
    const result: { month: number; year: number; label: string }[] = [];
    let m = ref.periodStartMonth;
    let y = ref.periodStartYear;
    const endM = ref.periodEndMonth;
    const endY = ref.periodEndYear;
    while (y < endY || (y === endY && m <= endM)) {
      result.push({
        month: m,
        year: y,
        label: this.months[m - 1].label + " " + y,
      });
      m++;
      if (m > 12) { m = 1; y++; }
    }
    this.budgetMonths = result;
  }

  loadAssignments() {
    const ref = this.budgetGroup.realistic || this.budgetGroup.pessimistic;
    if (!ref) return;
    this.apiBackend.getBudgetMonthAssignments(ref.id).subscribe(
      (res) => {
        this.monthAssignments.clear();
        this.comparisonMonths = [];
        for (const a of (res.body?.assignments || [])) {
          if (a.realizedProcessId) {
            this.monthAssignments.set(`${a.month}-${a.year}`, a.realizedProcessId);
            const monthLabel = this.months[a.month - 1]?.label || a.month;
            this.comparisonMonths.push({
              month: a.month,
              year: a.year,
              budgetId: ref.id,
              realizedProcessId: a.realizedProcessId,
              label: `${monthLabel} ${a.year}`,
            });
          }
        }
        if (this.comparisonMonths.length > 0) {
          this.loadComparison();
        }
      },
      () => {}
    );
  }

  getAssignment(month: number, year: number): number | null {
    return this.monthAssignments.get(`${month}-${year}`) || null;
  }

  onFileChange(month: number, year: number, processId: number | null) {
    const key = `${month}-${year}`;
    if (processId) {
      this.monthAssignments.set(key, processId);
    } else {
      this.monthAssignments.delete(key);
    }

    // Save to backend
    const ref = this.budgetGroup.realistic || this.budgetGroup.pessimistic;
    if (!ref) return;
    this.savingAssignment = true;
    this.apiBackend.updateBudgetMonthAssignments(ref.id, [
      { month, year, realizedProcessId: processId }
    ]).subscribe(
      () => { this.savingAssignment = false; },
      () => {
        this.savingAssignment = false;
        this.message.error("Eroare la salvarea atribuirii");
      }
    );
  }

  getFileLabel(f: any): string {
    if (f.month && f.year) {
      const monthLabel = this.months[f.month - 1]?.label || f.month;
      return `${monthLabel} ${f.year}`;
    }
    return f.fileName || 'Raport';
  }

  loadComparison() {
    if (!this.comparisonMonths.length) {
      this.message.warning('Adauga cel putin o luna pentru comparatie');
      return;
    }

    const budgetId = this.budgetGroup.realistic?.id || this.budgetGroup.pessimistic?.id;
    if (!budgetId) return;

    this.loadingComparison = true;

    // Save month assignments first
    const assignments = this.comparisonMonths.map(cm => ({
      month: cm.month,
      year: cm.year,
      realizedProcessId: cm.realizedProcessId,
    }));

    this.apiBackend.updateBudgetMonthAssignments(budgetId, assignments).subscribe(
      () => {
        // Then load comparison
        this.apiBackend.getBudgetFullComparison(budgetId).subscribe(
          (res) => {
            this.comparisonData = res.body;
            this.buildLookupMaps();
            this.buildChartData();
            this.loadingComparison = false;
          },
          () => {
            this.loadingComparison = false;
            this.message.error('Eroare la generarea comparatiei');
          }
        );
      },
      () => {
        this.loadingComparison = false;
        this.message.error('Eroare la salvarea asignarilor');
      }
    );
  }

  private buildLookupMaps() {
    this.rowLookup.clear();
    this.accountLookup.clear();
    this.assignedMonths = [];

    if (!this.comparisonData?.months) return;

    // Initialize expanded years: expand first two years by default
    const years: number[] = [...new Set<number>(this.comparisonData.months.map((m: any) => m.year))] as number[];
    this.expandedYears = new Set(years.slice(0, 2));

    for (const monthData of this.comparisonData.months) {
      const mk = `${monthData.month}-${monthData.year}`;
      const label = this.months[monthData.month - 1].label + " " + monthData.year;

      this.assignedMonths.push({
        month: monthData.month,
        year: monthData.year,
        label,
        hasRealized: !!monthData.hasRealized,
      });

      for (const row of monthData.rows) {
        this.rowLookup.set(`${row.sort_order}-${mk}`, row);
        if (row.accounts && row.accounts.length > 0) {
          this.accountLookup.set(`${row.sort_order}-${mk}`, row.accounts);
        }
      }
    }

    this.buildChartData();
  }

  private buildChartData() {
    this.chartChapters = [];
    this.selectedChartSort = null;
    this.barChartLabels = [];
    this.barChartDatasets = [];
    if (!this.comparisonData?.months?.length) return;

    const rows = this.comparisonData.months[0].rows;

    for (const row of rows) {
      if (row.is_computed) continue;
      this.chartChapters.push({ sort_order: row.sort_order, description: row.description });
    }

    if (this.chartChapters.length > 0) {
      this.selectedChartSort = this.chartChapters[0].sort_order;
      this.rebuildBarChart();
    }
  }

  onChartChapterChange() {
    this.rebuildBarChart();
  }

  private rebuildBarChart() {
    if (this.selectedChartSort === null) return;
    const hasRealistic = this.comparisonData?.has_realistic;
    const hasPessimistic = this.comparisonData?.has_pessimistic;

    this.barChartLabels = this.assignedMonths.map(mp => mp.label);

    const realized = this.assignedMonths.map(mp => {
      const r = this.rowLookup.get(`${this.selectedChartSort}-${mp.month}-${mp.year}`);
      return r?.realized ?? 0;
    });

    const datasets: ChartDataSets[] = [
      { data: realized, label: 'Realizat', backgroundColor: '#28B5B5', hoverBackgroundColor: '#1A8A8A' },
    ];

    if (hasRealistic) {
      datasets.push({
        data: this.assignedMonths.map(mp => {
          const r = this.rowLookup.get(`${this.selectedChartSort}-${mp.month}-${mp.year}`);
          return r?.realistic_budget ?? 0;
        }),
        label: 'Buget Realist',
        backgroundColor: '#8FD9A8',
        hoverBackgroundColor: '#5DC07A',
      });
    }

    if (hasPessimistic) {
      datasets.push({
        data: this.assignedMonths.map(mp => {
          const r = this.rowLookup.get(`${this.selectedChartSort}-${mp.month}-${mp.year}`);
          return r?.pessimistic_budget ?? 0;
        }),
        label: 'Buget Pesimist',
        backgroundColor: '#C996CC',
        hoverBackgroundColor: '#A85DAE',
      });
    }

    this.barChartDatasets = datasets;
  }

  getRowValue(sortOrder: number, month: number, year: number, field: string): any {
    const row = this.rowLookup.get(`${sortOrder}-${month}-${year}`);
    return row ? row[field] : null;
  }

  getAccounts(sortOrder: number, month: number, year: number): any[] {
    return this.accountLookup.get(`${sortOrder}-${month}-${year}`) || [];
  }

  onExpandChange(sortOrder: number, checked: boolean) {
    if (checked) {
      this.expandSet.add(sortOrder);
    } else {
      this.expandSet.delete(sortOrder);
    }
  }

  hasAnyAccounts(sortOrder: number): boolean {
    for (const mp of this.assignedMonths) {
      const accts = this.accountLookup.get(`${sortOrder}-${mp.month}-${mp.year}`);
      if (accts && accts.length > 0) return true;
    }
    return false;
  }

  /** Get unique accounts across all months for a sort_order (for row-per-account table) */
  getUniqueAccounts(sortOrder: number): { accountCode: string; accountDescription: string }[] {
    const seen = new Map<string, string>();
    for (const mp of this.assignedMonths) {
      const accts = this.accountLookup.get(`${sortOrder}-${mp.month}-${mp.year}`) || [];
      for (const a of accts) {
        if (!seen.has(a.accountCode)) {
          seen.set(a.accountCode, a.accountDescription);
        }
      }
    }
    return Array.from(seen.entries())
      .map(([code, desc]) => ({ accountCode: code, accountDescription: desc }))
      .sort((a, b) => a.accountCode.localeCompare(b.accountCode));
  }

  /** Get account value for a specific account code, month, year */
  getAccountValueForMonth(sortOrder: number, accountCode: string, month: number, year: number): number | null {
    const accts = this.accountLookup.get(`${sortOrder}-${month}-${year}`) || [];
    const acct = accts.find((a: any) => a.accountCode === accountCode);
    return acct ? acct.value : null;
  }

  /** Get any account field (value, budgetRealistic, realisticVar, etc.) */
  getAccountField(sortOrder: number, accountCode: string, month: number, year: number, field: string): any {
    const accts = this.accountLookup.get(`${sortOrder}-${month}-${year}`) || [];
    const acct = accts.find((a: any) => a.accountCode === accountCode);
    return acct ? (acct[field] ?? null) : null;
  }

  // Year expand/collapse for comparison table
  get comparisonYearGroups(): { year: number; monthCount: number }[] {
    const groups: { year: number; monthCount: number }[] = [];
    const seen = new Set<number>();
    for (const mp of this.assignedMonths) {
      if (!seen.has(mp.year)) {
        seen.add(mp.year);
        groups.push({ year: mp.year, monthCount: this.assignedMonths.filter(m => m.year === mp.year).length });
      }
    }
    return groups;
  }

  get hasMultipleComparisonYears(): boolean {
    return this.comparisonYearGroups.length > 1;
  }

  get visibleAssignedMonths(): { month: number; year: number; label: string; hasRealized?: boolean; isYearTotal?: boolean }[] {
    if (!this.hasMultipleComparisonYears) return this.assignedMonths;
    const result: any[] = [];
    for (const yg of this.comparisonYearGroups) {
      if (this.expandedYears.has(yg.year)) {
        result.push(...this.assignedMonths.filter(mp => mp.year === yg.year));
      } else {
        result.push({ month: 0, year: yg.year, label: `Total ${yg.year}`, isYearTotal: true });
      }
    }
    return result;
  }

  toggleComparisonYear(year: number) {
    if (this.expandedYears.has(year)) {
      this.expandedYears.delete(year);
    } else {
      this.expandedYears.add(year);
    }
  }

  getComparisonYearColspan(year: number): number {
    if (this.expandedYears.has(year)) {
      const monthCount = this.assignedMonths.filter(mp => mp.year === year).length;
      return monthCount * this.colsPerMonth;
    }
    return this.colsPerMonth; // collapsed = one set of sub-columns
  }

  get colsPerMonth(): number {
    let c = 1; // Realizat YTD
    if (this.comparisonData?.has_realistic) c += 2; // Buget Realist YTD + Variație Realist
    if (this.comparisonData?.has_pessimistic) c += 2; // Buget Pesimist YTD + Variație Pesimist
    return c;
  }

  /** Year total: all values are YTD, so use the last assigned month's value */
  getYearRowValue(sortOrder: number, year: number, field: string): number | null {
    const yearMonths = this.assignedMonths.filter(mp => mp.year === year);
    const lastMonth = yearMonths[yearMonths.length - 1];
    return lastMonth ? this.getRowValue(sortOrder, lastMonth.month, lastMonth.year, field) : null;
  }

  getYearAccountField(sortOrder: number, accountCode: string, year: number, field: string): number | null {
    const yearMonths = this.assignedMonths.filter(mp => mp.year === year);
    const lastMonth = yearMonths[yearMonths.length - 1];
    return lastMonth ? this.getAccountField(sortOrder, accountCode, lastMonth.month, lastMonth.year, field) : null;
  }

  /** Year total variation: realized at last month (already YTD) - sum of budget across year */
  getYearVariation(sortOrder: number, year: number, budgetField: string): number | null {
    const yearMonths = this.assignedMonths.filter(mp => mp.year === year);
    const lastMonth = yearMonths[yearMonths.length - 1];
    if (!lastMonth) return null;
    const realized = this.getRowValue(sortOrder, lastMonth.month, lastMonth.year, 'realized');
    const ytdBudget = this.getYearRowValue(sortOrder, year, budgetField);
    if (realized === null || ytdBudget === null) return null;
    return realized - ytdBudget;
  }

  /** Year total variation for account detail rows — treat missing realized as 0 */
  getYearAccountVariation(sortOrder: number, accountCode: string, year: number, budgetField: string): number | null {
    const yearMonths = this.assignedMonths.filter(mp => mp.year === year);
    const lastMonth = yearMonths[yearMonths.length - 1];
    if (!lastMonth) return null;
    const realizedValue = this.getAccountField(sortOrder, accountCode, lastMonth.month, lastMonth.year, 'value');
    const ytdBudget = this.getYearAccountField(sortOrder, accountCode, year, budgetField);
    if (ytdBudget === null) return null;
    return (realizedValue || 0) - ytdBudget;
  }

  getTableScrollX(): string {
    return (260 + this.visibleAssignedMonths.length * this.colsPerMonth * 120) + 'px';
  }

  /** All values from the backend are now YTD — return directly without summing */
  getYtdField(sortOrder: number, month: number, year: number, field: string): number | null {
    return this.getRowValue(sortOrder, month, year, field);
  }

  /** YTD variation: realized (YTD) - budget (YTD) */
  getYtdVariation(sortOrder: number, month: number, year: number, budgetField: string): number | null {
    const realized = this.getRowValue(sortOrder, month, year, 'realized');
    const budget = this.getRowValue(sortOrder, month, year, budgetField);
    if (realized === null || budget === null) return null;
    return realized - budget;
  }

  /** YTD for account detail rows — values are already YTD from backend */
  getYtdAccountField(sortOrder: number, accountCode: string, month: number, year: number, field: string = 'value'): number | null {
    return this.getAccountField(sortOrder, accountCode, month, year, field);
  }

  /** YTD variation for account detail rows — treat missing realized as 0 when budget exists */
  getYtdAccountVariation(sortOrder: number, accountCode: string, month: number, year: number, budgetField: string): number | null {
    const realizedValue = this.getAccountField(sortOrder, accountCode, month, year, 'value');
    const budget = this.getAccountField(sortOrder, accountCode, month, year, budgetField);
    if (budget === null) return null;
    return realizedValue - budget;
  }

  getComparisonRows(): any[] {
    if (!this.comparisonData?.months?.length) return [];
    return this.comparisonData.months[0].rows.map((r: any) => ({
      sort_order: r.sort_order,
      description: r.description,
      is_computed: r.is_computed,
    }));
  }

  exportComparisonExcel() {
    const ref = this.budgetGroup.realistic || this.budgetGroup.pessimistic;
    if (!ref) return;
    this.apiBackend.exportBudgetComparisonExcel(ref.id).subscribe(
      (res) => {
        const blob = res.body as Blob;
        saveAs(blob, `comparatie-${this.budgetGroup.name}.xlsx`);
      },
      () => this.message.error("Eroare la exportul Excel"),
    );
  }

  exportComparisonPdf() {
    const ref = this.budgetGroup.realistic || this.budgetGroup.pessimistic;
    if (!ref) return;
    this.apiBackend.exportBudgetComparisonPdf(ref.id).subscribe(
      (res) => {
        const blob = res.body as Blob;
        saveAs(blob, `comparatie-${this.budgetGroup.name}.pdf`);
      },
      () => this.message.error("Eroare la exportul PDF"),
    );
  }

  openComparisonEmailModal() {
    this.showEmailModal = true;
    this.emailAddress = '';
  }

  sendComparisonByEmail() {
    const ref = this.budgetGroup.realistic || this.budgetGroup.pessimistic;
    if (!ref || !this.emailAddress) return;
    this.sendingEmail = true;
    this.apiBackend.sendBudgetComparisonByEmail(ref.id, this.emailAddress).subscribe(
      () => {
        this.sendingEmail = false;
        this.showEmailModal = false;
        this.message.success("Email trimis cu succes");
      },
      () => {
        this.sendingEmail = false;
        this.message.error("Eroare la trimiterea emailului");
      },
    );
  }
}
